import NavigateNext from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import Breadcrumbs, { type BreadcrumbsProps } from '@mui/material/Breadcrumbs';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import { type SxProps, type Theme } from '@mui/material/styles';
import { ComponentProps, ElementType, ReactNode } from 'react';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import { BackButton, type BackButtonProps } from '../BackButton/BackButton';
import { StickyElement } from '../StickyElement/StickyElement';
import { Typography } from '../Typography/Typography';
import { TypographyIcon } from '../TypographyIcon/TypographyIcon';

export const getPagePadding = (isMobile = false) => (isMobile ? 2 : 3);
const DEFAULT_BREADCRUMB_ARIA_LABEL = 'breadcrumb';

export const Page = ({ children, fullWidth = true }: { children: ReactNode; fullWidth?: boolean }) => {
  const { isMobile } = useBreakpoint();
  return (
    <Container
      maxWidth={fullWidth ? false : 'lg'}
      sx={({ constants: { groundControlNavbarHeight }, palette }) => ({
        backgroundColor: palette.background.default,
        minHeight: `calc(100vh - ${groundControlNavbarHeight}px)`,
        p: getPagePadding(isMobile),
        position: 'relative',
      })}
    >
      {children}
    </Container>
  );
};

type TitleNonStickyProps = {
  sticky?: false | undefined;
  headerHeight?: never;
};

type TitleStickyProps = {
  sticky: true;
  headerHeight: number;
};

type TitleCommonProps = {
  title: string;
  subtitle?: string;
  icon?: ElementType;
  variant?: ComponentProps<typeof Typography>['variant'];
};

type TitleProps = TitleCommonProps & (TitleNonStickyProps | TitleStickyProps);

const PageTitle = ({ title, subtitle, icon, sticky, headerHeight, variant }: TitleProps) => {
  const { isMobile } = useBreakpoint();
  const baseSubtitleProps = {
    component: 'span',
    display: 'block',
    mt: 2,
    title: subtitle,
    variant: 'body2',
  } as const;

  const renderSubtitle = () => {
    if (!subtitle) return null;

    return icon ? (
      <TypographyIcon {...baseSubtitleProps} icon={icon}>
        {subtitle}
      </TypographyIcon>
    ) : (
      <Typography {...baseSubtitleProps}>{subtitle}</Typography>
    );
  };

  const titleContent = (
    <>
      <Typography variant={variant ?? (isMobile ? 'h6' : 'h4')} component="h1" noWrap>
        {title}
      </Typography>
      {renderSubtitle()}
    </>
  );

  if (sticky) {
    return <StickyElement headerHeight={headerHeight}>{titleContent}</StickyElement>;
  }

  return titleContent;
};

const PageDivider = () => <Divider sx={{ my: 2 }} />;

const PageBackButton = <C extends React.ElementType = 'button'>({
  children,
  ...rest
}: Omit<BackButtonProps<C>, 'sx'>) => {
  return (
    // @ts-expect-error - `sx` prop is not recognized by `BackButton`
    <BackButton<C> {...rest}>{children}</BackButton>
  );
};

type PageBreadcrumbsProps = {
  ariaLabel?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
} & Pick<BreadcrumbsProps, 'itemsAfterCollapse' | 'itemsBeforeCollapse' | 'maxItems'>;

const PageBreadcrumbs = ({
  ariaLabel,
  children,
  itemsAfterCollapse,
  itemsBeforeCollapse,
  maxItems,
  sx,
}: PageBreadcrumbsProps) => (
  <Breadcrumbs
    separator={<NavigateNext fontSize="small" />}
    aria-label={ariaLabel ?? DEFAULT_BREADCRUMB_ARIA_LABEL}
    itemsAfterCollapse={itemsAfterCollapse}
    itemsBeforeCollapse={itemsBeforeCollapse}
    maxItems={maxItems}
    sx={sx}
  >
    {children}
  </Breadcrumbs>
);

const PageActions = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      gap: 2,
      justifyContent: 'space-between',
      mb: 1.5,
    }}
  >
    {children}
  </Box>
);

Page.Title = PageTitle;

Page.Divider = PageDivider;

Page.BackButton = PageBackButton;

Page.Breadcrumbs = PageBreadcrumbs;

Page.Actions = PageActions;
